import { useMemo, useState } from 'react';
import PropTypes from 'prop-types';
import { useFieldArray } from 'react-hook-form';
import { PiTrashBold } from 'react-icons/pi';
import { useQuery } from '@tanstack/react-query';

import { cn } from '#/lib/utils';
import {
  Button,
  CompositeMenuButton,
  DESTRUCTIVE_GHOST_BUTTON_CLASS,
} from '#/modules/commonComponents/button';
import FloatingInput from '#/modules/commonComponents/form/FloatingInput';
import FloatingSelect from '#/modules/commonComponents/form/select';
import {
  fetchWarehouseLocationSelectOptions,
} from '#/modules/imsMgmt/receipts/stockReceipt/helper/locationSelectOptions';
import { lookupQueryKey } from '#/utils/queryKeys';

import WorkOrderInboundBatchAddPackagesDialog from './WorkOrderInboundBatchAddPackagesDialog';
import { DEFAULT_INBOUND_PACKAGE } from '../schemas/inboundSchema';

const WorkOrderInboundPackagesEditor = ({
  control,
  register,
  errors,
  warehouseId,
  disabled = false,
}) => {
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'packages',
  });
  const [batchOpen, setBatchOpen] = useState(false);

  const locationQuery = useQuery({
    queryKey: lookupQueryKey('warehouseLocationSelect', { warehouseId }),
    queryFn: () => fetchWarehouseLocationSelectOptions(warehouseId),
    enabled: Boolean(warehouseId),
  });

  const locationOptions = useMemo(
    () => locationQuery.data ?? [],
    [locationQuery.data],
  );

  function handleAddSingle() {
    if (disabled) {
      return;
    }
    append({ ...DEFAULT_INBOUND_PACKAGE });
  }

  function handleBatchConfirm(packages) {
    if (!packages?.length) {
      return;
    }
    append(packages);
  }

  return (
    <div className="mt-6 min-w-0 border-t border-dashed border-gray-300 pt-6">
      <div className="flex flex-wrap items-center gap-2">
        <h3 className="form-subsection__title mb-0!">包裝</h3>
        <CompositeMenuButton
          variant="default"
          label="新增包裝"
          disabled={disabled}
          caretTooltip="批次新增：一次加入多筆相同規格的包裝"
          caretAriaLabel="更多新增包裝選項"
          items={[
            {
              id: 'single',
              label: '新增單包裝',
              onSelect: handleAddSingle,
              disabled,
            },
            {
              id: 'batch',
              label: '批次新增',
              onSelect: () => setBatchOpen(true),
              disabled,
            },
          ]}
        />
      </div>

      {errors?.packages?.message ? (
        <p className="mt-2 text-sm text-destructive">{errors.packages.message}</p>
      ) : null}
      {errors?.packages?.root?.message ? (
        <p className="mt-2 text-sm text-destructive">{errors.packages.root.message}</p>
      ) : null}

      <div className="mt-3 stock-ops-panel-scroll">
        {fields.length === 0 ? (
          <p className="py-4 text-center text-sm text-muted-foreground">
            尚未新增包裝
          </p>
        ) : (
          <div className={cn('grid grid-cols-1 gap-2', '@2xl/sheet:grid-cols-2')}>
            {fields.map((field, index) => (
              <div
                key={field.id}
                className="min-w-0 space-y-2 rounded-md border border-slate-200 bg-slate-50 px-2 py-2"
              >
                <div className="flex items-center justify-between gap-2">
                  <p className="text-xs font-medium text-slate-500">
                    包裝
                    {index + 1}
                  </p>
                  {fields.length > 1 ? (
                    <Button
                      type="button"
                      size="icon-sm"
                      variant="ghost"
                      className={DESTRUCTIVE_GHOST_BUTTON_CLASS}
                      aria-label={`刪除包裝 ${index + 1}`}
                      onClick={() => remove(index)}
                      disabled={disabled}
                    >
                      <PiTrashBold className="size-4" aria-hidden />
                    </Button>
                  ) : null}
                </div>
                <div className="grid grid-cols-1 gap-2 sm:grid-cols-2">
                  <FloatingInput
                    id={`packages.${index}.qty`}
                    label="數量"
                    type="number"
                    register={register}
                    errors={errors}
                    disabled={disabled}
                  />
                  <FloatingSelect
                    id={`packages.${index}.locationId`}
                    label="儲位（可選）"
                    options={locationOptions}
                    control={control}
                    errors={errors}
                    allowEmpty
                    isClearable
                    isDisabled={
                      disabled || !warehouseId || locationQuery.isLoading
                    }
                    noOptionsMessage={
                      warehouseId ? '此倉別尚無儲位' : '請先選擇入庫倉'
                    }
                  />
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      <WorkOrderInboundBatchAddPackagesDialog
        open={batchOpen}
        onOpenChange={setBatchOpen}
        onConfirm={handleBatchConfirm}
        warehouseId={warehouseId}
        locationOptions={locationOptions}
        locationOptionsLoading={locationQuery.isLoading}
      />
    </div>
  );
};

WorkOrderInboundPackagesEditor.propTypes = {
  control: PropTypes.object.isRequired,
  register: PropTypes.func.isRequired,
  errors: PropTypes.object,
  warehouseId: PropTypes.string,
  disabled: PropTypes.bool,
};

export default WorkOrderInboundPackagesEditor;
